import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "./ui/button";
import { 
  X, 
  CreditCard, 
  Check,
  Loader2,
  Shield,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const WalletTopUpModal = ({ isOpen, onClose, wallet, onSuccess }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch top-up packages
  const { data: packagesData } = useQuery({
    queryKey: ["stripePackages"],
    queryFn: async () => {
      const response = await axios.get(`${API}/payments/stripe/packages`);
      return response.data;
    },
    enabled: isOpen
  });

  const packages = packagesData?.packages || {};
  const eligibleCountries = packagesData?.eligible_countries || [];

  // Create checkout session
  const createCheckout = useMutation({
    mutationFn: async (packageId) => {
      const response = await axios.post(`${API}/payments/stripe/checkout`, {
        package_id: packageId,
        wallet_id: wallet?.wallet_id,
        origin_url: window.location.origin
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    },
    onError: (error) => {
      setIsProcessing(false);
      toast.error(error.response?.data?.detail || "Failed to create checkout session");
    }
  });

  const handleTopUp = () => {
    if (!selectedPackage) {
      toast.error("Please select an amount");
      return;
    }
    setIsProcessing(true);
    createCheckout.mutate(selectedPackage);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="relative w-full max-w-md mx-4 bg-[#1a1a2e] rounded-3xl overflow-hidden shadow-2xl border border-white/10 animate-in fade-in zoom-in-95 duration-200"
        data-testid="topup-modal"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Top Up Wallet</h2>
                <p className="text-xs text-muted-foreground">via Stripe (Card)</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              data-testid="close-topup-modal"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Wallet Info */}
          {wallet && (
            <div className="bg-white/5 rounded-2xl p-4">
              <p className="text-xs text-muted-foreground mb-1">Adding to</p>
              <p className="text-lg font-semibold text-white">
                {wallet.currency} Wallet
              </p>
              <p className="text-sm text-muted-foreground">
                Current Balance: {wallet.currency === "USD" ? "$" : wallet.currency}{wallet.balance?.toFixed(2)}
              </p>
            </div>
          )}

          {/* Package Selection */}
          <div>
            <p className="text-sm font-medium text-white mb-3">Select Amount</p>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(packages).map(([id, pkg]) => (
                <button
                  key={id}
                  onClick={() => setSelectedPackage(id)}
                  className={`relative p-4 rounded-xl border-2 transition-all duration-200 ${
                    selectedPackage === id
                      ? "border-emerald-500 bg-emerald-500/10"
                      : "border-white/10 bg-white/5 hover:border-white/20"
                  }`}
                  data-testid={`package-${id}`}
                >
                  <p className="text-2xl font-bold text-white">{pkg.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">{pkg.currency}</p>
                  {selectedPackage === id && (
                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Country Notice */}
          <div className="flex items-start gap-3 p-3 bg-amber-500/10 rounded-xl border border-amber-500/20">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-amber-200">
                Card payments available for: {eligibleCountries.slice(0, 5).join(", ")}
                {eligibleCountries.length > 5 && ` +${eligibleCountries.length - 5} more`}
              </p>
            </div>
          </div>

          {/* Security Badge */}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Shield className="w-4 h-4" />
            <span>Secured by Stripe</span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <Button
            onClick={handleTopUp}
            disabled={!selectedPackage || isProcessing}
            className="w-full h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-base disabled:opacity-50"
            data-testid="proceed-topup-btn"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Redirecting to Stripe...
              </>
            ) : (
              <>
                Continue to Payment
                {selectedPackage && packages[selectedPackage] && (
                  <span className="ml-2 opacity-80">
                    ({packages[selectedPackage].label})
                  </span>
                )}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WalletTopUpModal;
